import { Menu } from "@base-ui/react/menu"
import classNames from "classnames"
import PropTypes from "prop-types"
import React from "react"

import SideSheet from "src/components/SideSheet"
import Tooltip from "src/components/Tooltip"

// compact sits in table rows; standalone matches the square ghost buttons beside it in a header.
const TRIGGER_CLASSES = {
  compact:
    "flex cursor-pointer items-center justify-center rounded bg-white p-0 hover:bg-gray-200",
  standalone:
    "btn-ghost-tertiary btn-icon-only inline-flex items-center justify-center",
}

const ActionMenuItem = ({
  label,
  icon,
  iconStyle = "kit",
  onClick,
  href,
  disabled,
  disabledReason,
}) => {
  const styles = classNames(
    "flex w-full items-center gap-2 px-3 py-2 text-sm text-left text-gray-700 no-underline hover:no-underline",
    disabled
      ? "opacity-50 cursor-not-allowed"
      : "cursor-pointer hover:bg-gray-100"
  )

  const content = (
    <>
      {icon && (
        <div className="flex w-6 justify-center text-center">
          <i className={`fa-${iconStyle} fa-${icon} text-base`} />
        </div>
      )}
      {label}
    </>
  )

  const item = href ? (
    <Menu.Item
      render={<a href={href} />}
      onClick={onClick}
      className={styles}
      disabled={disabled}
    >
      {content}
    </Menu.Item>
  ) : (
    <Menu.Item onClick={onClick} className={styles} disabled={disabled}>
      {content}
    </Menu.Item>
  )

  if (!disabled || !disabledReason) return item

  return (
    <Tooltip
      text={disabledReason}
      placement="left"
      variant="dark"
      maxWidth="300px"
    >
      {item}
    </Tooltip>
  )
}

ActionMenuItem.propTypes = {
  label: PropTypes.string.isRequired,
  icon: PropTypes.string,
  iconStyle: PropTypes.string,
  onClick: PropTypes.func,
  href: PropTypes.string,
  disabled: PropTypes.bool,
  disabledReason: PropTypes.string,
}

const ActionMenu = ({
  items,
  triggerIcon,
  triggerIconStyle,
  triggerLabel = "Actions",
  triggerVariant = "compact",
  side = "bottom",
  align = "end",
  onOpenChange,
}) => {
  const popupContainer = SideSheet.usePopupContainer()

  if (!items || items.length === 0) {
    return null
  }

  const resolvedTriggerIcon = triggerIcon || "ellipsis-vertical"
  // The default trigger icon is a Classic solid icon; any icon a caller
  // passes explicitly is assumed to be from the kit unless they also
  // specify triggerIconStyle.
  const resolvedTriggerIconStyle =
    triggerIconStyle || (triggerIcon ? "kit" : "solid")

  return (
    <Menu.Root onOpenChange={onOpenChange}>
      <Menu.Trigger
        aria-label={triggerLabel}
        className={TRIGGER_CLASSES[triggerVariant]}
      >
        <i
          className={classNames(
            `fa-${resolvedTriggerIconStyle} fa-${resolvedTriggerIcon}`,
            triggerVariant === "compact" && "text-xl text-gray-900"
          )}
        />
      </Menu.Trigger>

      <Menu.Portal container={popupContainer ?? undefined}>
        <Menu.Positioner
          side={side}
          align={align}
          sideOffset={4}
          className="z-[100]"
        >
          <Menu.Popup className="min-w-44 rounded border bg-white py-1 shadow-lg">
            {items.map((item, index) => (
              <ActionMenuItem key={item.label} {...item} />
            ))}
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  )
}

ActionMenu.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      icon: PropTypes.string,
      iconStyle: PropTypes.string,
      onClick: PropTypes.func,
      href: PropTypes.string,
      disabled: PropTypes.bool,
      disabledReason: PropTypes.string,
    })
  ).isRequired,
  triggerIcon: PropTypes.string,
  triggerIconStyle: PropTypes.string,
  triggerLabel: PropTypes.string,
  triggerVariant: PropTypes.oneOf(["compact", "standalone"]),
  side: PropTypes.oneOf(["top", "bottom", "left", "right"]),
  align: PropTypes.oneOf(["start", "center", "end"]),
  onOpenChange: PropTypes.func,
}

export default ActionMenu
